<template>
  <div>
    <div class="list-toolbar">
      <el-select
        v-model="selectedManufacturerId"
        placeholder="Select manufacturer"
        size="small"
        filterable
        class="manufacturer-select"
      >
        <el-option
          v-for="m in safeManufacturers"
          :key="m.id"
          :label="m.name"
          :value="m.id"
        />
      </el-select>
      <el-input
        v-model="filter"
        size="small"
        prefix-icon="el-icon-search"
        placeholder="filter by product number or name"
        clearable
        class="list-filter"
      ></el-input>
      <el-button
        size="small"
        icon="el-icon-refresh"
        :loading="loading"
        @click="getData"
        >Refresh</el-button
      >
    </div>
    <div class="app-card">
      <el-table
        border
        ref="table"
        :data="tableData"
        size="small"
        v-loading="loading"
        class="app-table"
        :max-height="tableHeight"
      >
        <el-table-column
          :label="$t('text.product_number')"
          prop="productNumber"
        />
        <el-table-column
          :label="$t('text.name') | firstLetterCapitalize"
          prop="productName"
        />
        <el-table-column
          :label="$t('text.description') | firstLetterCapitalize"
          prop="description"
        />
        <el-table-column
          :label="$t('text.working_capacity') + ' (ml)'"
          prop="workingCapacity"
          width="150"
        />
        <el-table-column label="Max Sensor" prop="maxSensor" width="110" />
        <el-table-column
          label="Clumping Enabled"
          prop="isClumpingEnabled"
          width="140"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag
              :type="row.isClumpingEnabled ? 'success' : 'info'"
              size="small"
              class="status-tag"
            >
              {{ row.isClumpingEnabled ? 'Yes' : 'No' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="Max Allowed Time"
          prop="predictedTimeFormat"
          width="140"
        />
        <el-table-column
          v-if="p('Products.Product Description.Edit')"
          :label="$t('text.action') | firstLetterCapitalize"
          min-width="110"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button
              size="small"
              type="primary"
              plain
              @click="edit(scope.row)"
            >
              {{ $t('text.view') | firstLetterCapitalize }}/{{
                $t('text.edit') | firstLetterCapitalize
              }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <EditProduct ref="editProduct" />
  </div>
</template>

<script>
import EditProduct from '@/components/products/products/EditProduct'
import ApiService from '@/services/CommonApiService.js'
import loraTableHeight from '@/mixins/loraTableHeight'
import { secondFormat } from '@/utils/utils'
export default {
  mixins: [loraTableHeight],
  data() {
    return {
      loading: false,
      tableData: [],
      filter: '',
      timer: ''
    }
  },
  components: {
    EditProduct
  },
  computed: {
    manufacturers() {
      return this.$store.state.manufacturer.manufacturers || []
    },
    safeManufacturers() {
      const list = this.manufacturers
      const sel = this.$store.state.manufacturer.selectedManufacturer
      if (sel && !list.find(m => m.id === sel.id)) return [sel, ...list]
      return list
    },
    selectedManufacturerId: {
      get() {
        const m = this.$store.state.manufacturer.selectedManufacturer
        return m ? m.id : null
      },
      set(id) {
        const m = this.manufacturers.find(x => x.id === id)
        if (m) this.$store.commit('manufacturer/SET_SELECTED_MANUFACTURER', m)
      }
    }
  },
  mounted() {
    this.$store.dispatch('manufacturer/fetchManufacturer').then(() => {
      this.getData()
    })
    if (
      this.p('Products.Product Description.Add') &&
      this.$parent.setPageAction
    ) {
      this.$parent.setPageAction({
        label: 'Add Product',
        onClick: this.add
      })
    }
  },
  beforeDestroy() {
    if (this.$parent.clearPageAction) this.$parent.clearPageAction()
  },
  watch: {
    '$store.state.manufacturer.selectedManufacturer'() {
      this.getData()
    },
    filter() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(this.getData, 500)
    }
  },
  methods: {
    getData() {
      if (!this.$store.state.manufacturer.selectedManufacturer) return
      this.loading = true
      ApiService.GetProductsByManufacturerId(
        this.$store.state.manufacturer.selectedManufacturer.id
      )
        .then(response => {
          this.loading = false
          this.tableData = response.data.data.map(item => {
            const predictedTime = secondFormat(item.predictedTime)
            let predictedTimeFormat = ''
            if (predictedTime[0]) predictedTimeFormat += predictedTime[0] + 'd'
            if (predictedTime[1]) predictedTimeFormat += predictedTime[1] + 'h'
            if (predictedTime[2]) predictedTimeFormat += predictedTime[2] + 'm'
            if (predictedTime[3]) predictedTimeFormat += predictedTime[3] + 's'
            return { ...item, predictedTimeFormat }
          })
          if (this.filter) {
            this.tableData = this.tableData.filter(
              item =>
                item.productNumber
                  .toLowerCase()
                  .indexOf(this.filter.toLowerCase()) > -1 ||
                item.productName
                  .toLowerCase()
                  .indexOf(this.filter.toLowerCase()) > -1
            )
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    add() {
      this.$refs.editProduct.openDialog()
    },
    edit(row) {
      this.$refs.editProduct.openDialog()
      const data = JSON.parse(JSON.stringify(row))
      data.predictedTimeH = Math.floor(row.predictedTime / 3600)
      data.predictedTimeM = Math.floor((row.predictedTime % 3600) / 60)
      this.$refs.editProduct.form = data
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.list-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.manufacturer-select {
  width: 200px;
}

.list-filter {
  width: 220px;
}

.app-table {
  /deep/ .status-tag {
    border-radius: 99px;
    border: none;
    font-weight: 600;
  }
}
</style>
